// ============================================
// textbookService.js - Textbook Intelligence API
// ============================================
// API service for textbook upload, search, and querying

import { apiRequest } from './api';

// ============================================
// TEXTBOOK API ENDPOINTS
// ============================================

export const textbookService = {
  /**
   * List all user's textbooks
   * GET /api/txt/textbooks/
   */
  listTextbooks: () => apiRequest('/txt/textbooks/'),

  /**
   * Upload a new textbook
   * POST /api/txt/textbooks/upload/
   * @param {File} file - The PDF file to upload
   * @param {string} title - Optional title override
   * @param {string} subject - Optional subject
   * @param {boolean} async - Whether to process async (default: true)
   */
  uploadTextbook: async (file, title = '', subject = '', processAsync = true) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (subject) formData.append('subject', subject);
    formData.append('async', processAsync.toString());

    // Use fetch directly for FormData (don't set Content-Type, browser will set it with boundary)
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const token = localStorage.getItem('ghostink_access_token');

    try {
      const response = await fetch(`${API_BASE}/txt/textbooks/upload/`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data?.error || data?.detail || 'Upload failed',
          status: response.status,
          data,
        };
      }

      return { data, status: response.status };
    } catch (e) {
      console.error('Upload failed:', e);
      return { error: e.message || 'Network error', status: 0 };
    }
  },

  /**
   * Get textbook processing status
   * GET /api/txt/textbooks/{id}/
   * @param {string} textbookId - UUID of the textbook
   */
  getTextbookStatus: (textbookId) => 
    apiRequest(`/txt/textbooks/${textbookId}/`),

  /**
   * Query a textbook with a question (basic RAG)
   * POST /api/txt/textbooks/{id}/query/
   * @param {string} textbookId - UUID of the textbook
   * @param {string} question - The question to ask
   * @param {string} style - Response style: 'student' | 'essay' | 'homework'
   * @param {boolean} generate - Whether to generate AI response
   */
  queryTextbook: (textbookId, question, style = 'student', generate = true) =>
    apiRequest(`/txt/textbooks/${textbookId}/query/`, {
      method: 'POST',
      body: JSON.stringify({ question, style, generate }),
    }),

  /**
   * Enhanced RAG query with rewriting, re-ranking, and knowledge graph
   * POST /api/txt/textbooks/{id}/enhanced-query/
   * @param {string} textbookId - UUID of the textbook
   * @param {string} question - The question to ask
   * @param {string} chapter - Optional chapter context
   * @param {number} topK - Number of results
   * @param {boolean} includeConcepts - Include related concepts from knowledge graph
   */
  enhancedQuery: (textbookId, question, chapter = null, topK = 5, includeConcepts = true) =>
    apiRequest(`/txt/textbooks/${textbookId}/enhanced-query/`, {
      method: 'POST',
      body: JSON.stringify({ 
        question, 
        chapter,
        top_k: topK,
        include_concepts: includeConcepts 
      }),
    }),

  /**
   * Search within a textbook
   * POST /api/txt/textbooks/{id}/search/
   * @param {string} textbookId - UUID of the textbook
   * @param {string} query - Search query
   * @param {number} topK - Number of results (default: 5)
   * @param {string} chapter - Optional chapter filter
   */
  searchTextbook: (textbookId, query, topK = 5, chapter = null) =>
    apiRequest(`/txt/textbooks/${textbookId}/search/`, {
      method: 'POST',
      body: JSON.stringify({ query, top_k: topK, chapter }),
    }),

  /**
   * Get all definitions from a textbook
   * GET /api/txt/textbooks/{id}/definitions/
   * @param {string} textbookId - UUID of the textbook
   */
  getDefinitions: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/definitions/`),

  /**
   * Get chapter list from a textbook
   * GET /api/txt/textbooks/{id}/chapters/
   * @param {string} textbookId - UUID of the textbook
   */
  getChapters: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/chapters/`),

  /**
   * Build knowledge graph for a textbook
   * POST /api/txt/textbooks/{id}/build-kg/
   * @param {string} textbookId - UUID of the textbook
   */
  buildKnowledgeGraph: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/build-kg/`, {
      method: 'POST',
    }),

  /**
   * Get knowledge graph data for visualization
   * GET /api/txt/textbooks/{id}/knowledge-graph/
   * @param {string} textbookId - UUID of the textbook
   */
  getKnowledgeGraph: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/knowledge-graph/`),

  /**
   * Get related concepts from knowledge graph
   * GET /api/txt/textbooks/{id}/related-concepts/?concept=X&depth=2
   * @param {string} textbookId - UUID of the textbook
   * @param {string} concept - The concept to find relations for
   * @param {number} depth - How many hops to traverse
   */
  getRelatedConcepts: (textbookId, concept, depth = 2) =>
    apiRequest(`/txt/textbooks/${textbookId}/related-concepts/?concept=${encodeURIComponent(concept)}&depth=${depth}`),

  /**
   * Enhance chunks with AI summaries
   * POST /api/txt/textbooks/{id}/enhance-chunks/
   * @param {string} textbookId - UUID of the textbook
   */
  enhanceChunks: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/enhance-chunks/`, {
      method: 'POST',
    }),

  /**
   * Rate a query response (for training data)
   * POST /api/txt/queries/{id}/rate/
   * @param {string} queryId - UUID of the query
   * @param {number} rating - Rating 1-5
   * @param {boolean} helpful - Was it helpful?
   * @param {string} feedback - Optional feedback text
   */
  rateResponse: (queryId, rating, helpful, feedback = '') =>
    apiRequest(`/txt/queries/${queryId}/rate/`, {
      method: 'POST',
      body: JSON.stringify({ rating, helpful, feedback }),
    }),

  /**
   * Delete a textbook
   * DELETE /api/txt/textbooks/{id}/
   * @param {string} textbookId - UUID of the textbook
   */
  deleteTextbook: (textbookId) =>
    apiRequest(`/txt/textbooks/${textbookId}/`, {
      method: 'DELETE',
    }),
};

// ============================================
// POLLING HELPER
// ============================================

/**
 * Poll for textbook processing status
 * @param {string} textbookId - UUID of the textbook
 * @param {function} onProgress - Callback for status updates
 * @param {function} onComplete - Callback when processing completes
 * @param {function} onError - Callback on error
 * @param {number} interval - Polling interval in ms (default: 2000)
 * @param {number} maxAttempts - Max polling attempts (default: 150 = 5 minutes)
 * @returns {function} Cancel function to stop polling
 */
export function pollTextbookStatus(
  textbookId,
  onProgress,
  onComplete,
  onError,
  interval = 2000,
  maxAttempts = 150
) {
  let attempts = 0;
  let cancelled = false;

  const poll = async () => {
    if (cancelled) return;
    
    attempts++;
    
    if (attempts > maxAttempts) {
      onError?.({ error: 'Processing timeout - please check back later' });
      return;
    }

    try {
      const result = await textbookService.getTextbookStatus(textbookId);

      if (result.error) {
        onError?.(result);
        return;
      }

      const { status, progress } = result.data;

      // Report progress
      onProgress?.(result.data);

      if (status === 'ready') {
        onComplete?.(result.data);
        return;
      }

      if (status === 'failed') {
        onError?.({ error: result.data.error || 'Processing failed' });
        return;
      }

      // Still processing - continue polling
      if (!cancelled) {
        setTimeout(poll, interval);
      }
    } catch (e) {
      onError?.({ error: e.message || 'Network error' });
    }
  };

  // Start polling
  poll();

  // Return cancel function
  return () => {
    cancelled = true;
  };
}

export default textbookService;
